import { ClockIcon, LocationIcon } from "@cpj-ai/panel-npm-package";
import { OnboardingJobSuggestionType } from "@graphql/generated";
import { Skeleton } from "@heroui/skeleton";
import { motion } from "framer-motion";

interface JobPostCardProps {
  job?: OnboardingJobSuggestionType | null;
  index: number;
  handleContinue: () => void;
  isLoadingContinue: boolean;
}

const formatString = (str: string) => {
  return str.replace(/([A-Z])/g, " $1").trim();
};

const getRandomContractType = () => {
  const types = ["Contract", "Full-time", "Part-time"];
  return types[Math.floor(Math.random() * types.length)];
};

const getRandomPostedDate = (index: number) => {
  const baseDays = 1;
  const additionalDays = index * (Math.random() < 0.5 ? 1 : 2);
  const totalDays = baseDays + additionalDays;

  return `Posted ${totalDays} ${totalDays === 1 ? "day" : "days"} ago`;
};

export const JobPostCardSkeleton = ({ index }: { index: number }) => {
  return (
    <motion.div
      key={`skeleton-${index}`}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 1.7 + index * 0.15 }}
      className="group"
    >
      <div className="relative backdrop-blur-sm bg-white/90 rounded-2xl overflow-hidden border border-gray-100">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-transparent to-transparent opacity-90"></div>
        <div className="absolute left-0 top-6 bottom-6 w-1 bg-primary-500 rounded-r-full"></div>

        <div className="p-3 sm:p-4 md:p-6 relative">
          <div className="flex flex-col gap-3 sm:gap-4">
            <div className="flex items-start gap-3">
              <Skeleton className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-default-200" />
              </Skeleton>

              <div className="space-y-2 flex-1 min-w-0">
                <Skeleton className="w-full max-w-48 rounded-lg">
                  <div className="h-5 sm:h-6 w-full max-w-48 rounded-lg bg-default-200" />
                </Skeleton>
                <div className="flex flex-col sm:flex-row gap-1 sm:gap-2">
                  <Skeleton className="w-32 rounded-lg">
                    <div className="h-4 w-32 rounded-lg bg-default-200" />
                  </Skeleton>
                  <Skeleton className="w-20 rounded-lg">
                    <div className="h-4 w-20 rounded-lg bg-default-200" />
                  </Skeleton>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
              <Skeleton className="w-24 rounded-lg">
                <div className="h-4 w-24 rounded-lg bg-default-200" />
              </Skeleton>
              <Skeleton className="w-32 rounded-lg">
                <div className="h-4 w-32 rounded-lg bg-default-200" />
              </Skeleton>
            </div>
          </div>

          <div className="mt-3 sm:mt-4 pt-2 sm:pt-3 border-t border-gray-100 flex justify-end items-center">
            <Skeleton className="w-20 rounded-lg">
              <div className="h-3 w-20 rounded-lg bg-default-200" />
            </Skeleton>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const JobPostCard = ({ job, index, handleContinue, isLoadingContinue }: JobPostCardProps) => {
  if (!job) return <JobPostCardSkeleton index={index} />;

  return (
    <div
      key={job.companyName}
      className="group cursor-pointer w-full"
      onClick={isLoadingContinue ? undefined : handleContinue}
    >
      <div className="relative backdrop-blur-sm bg-white/90 rounded-2xl overflow-hidden border border-gray-100 transition-all duration-300 hover:shadow-md">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-transparent to-transparent opacity-90"></div>
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-r from-primary-50/50 to-transparent transition-opacity duration-300"></div>

        <div className="absolute left-0 top-6 bottom-6 w-1 bg-primary-500 rounded-r-full group-hover:top-0 group-hover:bottom-0 transition-all duration-300"></div>

        <div className="p-3 sm:p-4 md:p-6 relative">
          <div className="flex flex-col gap-3 sm:gap-4">
            <div className={`flex items-start gap-3 ${index === 0 ? "mt-2 md:mt-0" : ""}`}>
              <div className="bg-gradient-to-br from-primary-50 to-primary-100 w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center text-primary-600 font-bold shadow-sm group-hover:shadow-md transition-all flex-shrink-0">
                {job.companyName?.charAt(0) ?? "?"}
              </div>

              <div className="flex-1 min-w-0 overflow-hidden">
                <h4 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 group-hover:text-primary-700 transition-colors text-start truncate leading-tight text-wrap">
                  {job.title}
                </h4>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs sm:text-sm text-gray-500 truncate">{job.companyName}</span>
                  <span className="min-w-fit text-xs py-0.5 px-2 bg-gray-100 text-gray-600 rounded-md group-hover:bg-primary-100 group-hover:text-primary-700 transition-colors self-start">
                    {formatString(getRandomContractType() ?? "")}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2 sm:gap-3">
              <div className="flex items-center text-gray-600 min-w-0">
                <LocationIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 fill-gray-400 flex-shrink-0" />
                <span className="text-xs sm:text-sm truncate">{job.country}</span>
              </div>

              {(job.salaryRange && job.salaryRange?.[0] !== 0 && job.salaryRange?.[1] !== 0 && job.salaryRange?.[0]) ||
              job.salaryRange?.[1] ? (
                <div className="flex items-center text-gray-600 min-w-0">
                  <svg
                    className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 text-gray-400 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="text-xs sm:text-sm font-medium truncate">
                    ${job.salaryRange?.[0]?.toLocaleString()} - ${job.salaryRange?.[1]?.toLocaleString()}
                  </span>
                </div>
              ) : (
                <div className="flex items-center text-gray-600">
                  <span className="text-xs font-medium">Salary not disclosed</span>
                </div>
              )}
            </div>
          </div>

          <div className="mt-3 sm:mt-4 pt-2 sm:pt-3 border-t border-gray-100 flex justify-end items-center">
            <div className="text-xs text-gray-500 flex items-center">
              <ClockIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1 sm:mr-1.5 fill-gray-400" />
              {getRandomPostedDate(index)}
            </div>
          </div>

          {index === 0 && (
            <div className="absolute top-0 right-0">
              <div className="bg-green-500 text-white text-xs font-medium py-1 px-3 rounded-bl-lg">NEW</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobPostCard;
